import type {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { config } from "../../config";
import { createLogger, redact } from "../../logger";

const httpLog = createLogger("http:client");

// Console gate, routed through @core/config (the single env source). Dev-only console output;
// in prod this is `false` and only the error-sink path (log.error) runs.
const CONSOLE_ENABLED = config.isDev;

/** Request config with our timing stamp attached. */
interface TimedConfig extends InternalAxiosRequestConfig {
  meta?: { start: number };
}

/** Drop the query string so tokens that ride in query params never get logged. */
function pathOf(url: string | undefined): string {
  if (!url) return "";
  const q = url.indexOf("?");
  return q === -1 ? url : url.slice(0, q);
}

function elapsed(config: TimedConfig | undefined): number {
  return config?.meta ? Date.now() - config.meta.start : 0;
}

function methodOf(config: TimedConfig | undefined): string {
  return (config?.method ?? "get").toUpperCase();
}

/**
 * Attach request/response logging to an axios instance.
 *
 * REDACTION: this NEVER logs Authorization headers, cookies, or request bodies (which may
 * carry passwords/tokens). It logs method, path (query stripped), status, and timing; the
 * only body it touches is the error RESPONSE body, run through `redact()` first.
 *
 * Install this BEFORE the auth interceptor so, on an error, this observer runs first, logs
 * the RAW AxiosError, and re-throws it untouched — leaving the 401→refresh interceptor to
 * handle recovery. It never swallows an error.
 */
export function installHttpLogging(instance: AxiosInstance): void {
  instance.interceptors.request.use((config) => {
    (config as TimedConfig).meta = { start: Date.now() };
    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      logSuccess(response);
      return response;
    },
    (error: AxiosError) => {
      logFailure(error);
      // Observe, don't swallow — re-throw the raw error for the auth interceptor.
      return Promise.reject(error);
    }
  );
}

function logSuccess(response: AxiosResponse): void {
  if (!CONSOLE_ENABLED) return; // success logging is dev/staging only; silent in prod
  const config = response.config as TimedConfig;
  const header = `[http:client] ${methodOf(config)} ${pathOf(config.url)} → ${response.status} (${elapsed(config)}ms)`;
  console.groupCollapsed(header);
  console.groupEnd();
}

function logFailure(error: AxiosError): void {
  const config = error.config as TimedConfig | undefined;
  const method = methodOf(config);
  const path = pathOf(config?.url);
  const status = error.response?.status ?? 0; // 0 => network / no response
  const ms = elapsed(config);
  const isServerOrNetwork = status === 0 || status >= 500;

  if (CONSOLE_ENABLED) {
    // Expanded group for 4xx/5xx. Only the response body is shown, and only redacted —
    // headers and the request body are never logged.
    console.group(
      `[http:client] ${method} ${path} → ${status || "network"} (${ms}ms)`
    );
    if (error.response?.data !== undefined) {
      console.error("response:", redact(error.response.data));
    }
    console.groupEnd();
    return;
  }

  // Prod: no console. Server/network failures still reach the error sink (redacted inside
  // log.error). 4xx are client-deterministic and handled by callers — not sink-worthy.
  if (isServerOrNetwork) {
    httpLog.error(`${method} ${path} failed`, {
      context: "http",
      method,
      path,
      status,
      durationMs: ms,
    });
  }
}
